import { z } from "zod";
import { completionSourceFingerprintSchema } from "./completionSourceFingerprint";
import { legacyWorkflowJobId, legacyWorkflowSourceId, trackedJobIdSchema, type LegacyWorkflowReference } from "./legacyWorkflowIdentity";

const effectSchema = z.object({
  jobId: trackedJobIdSchema,
  workflowResultId: trackedJobIdSchema.optional(),
  sourceFingerprint: completionSourceFingerprintSchema,
  effectId: z.string().regex(/^component:(0|[1-9][0-9]{0,2})$/),
}).strict();
export const completionComponentSourceSchema = effectSchema;
const sourcesSchema = z.object({ ownerId: z.string().min(1).max(256), effects: z.array(effectSchema).max(100_000) }).strict();
export type CompletionComponentSource = z.infer<typeof effectSchema>;
type Source = { job: { jobId: string }; sourceFingerprint: string; storageParent?: { id: string } };

/** Cache ordinals belong to one immutable source, independently of response grouping. */
export function completionComponentResponse(ownerId: string, results: readonly { source: Source; presentation: { components: Record<string, unknown>[] } }[]) {
  const components: Record<string, unknown>[] = [];
  const effects: z.infer<typeof effectSchema>[] = [];
  for (const { source, presentation } of results) {
    presentation.components.forEach((component, index) => {
      components.push(component);
      effects.push({ jobId: source.job.jobId, sourceFingerprint: source.sourceFingerprint, effectId: `component:${index}`,
        ...(source.storageParent ? { workflowResultId: source.storageParent.id } : {}) });
    });
  }
  return { components, componentSources: sourcesSchema.parse({ ownerId, effects }) };
}

export function resolveCompletionComponentEffects(input: {
  components: unknown; componentSources?: unknown; ownerId: string;
  jobs: readonly { jobId: string; legacySource?: LegacyWorkflowReference }[];
}) {
  if (!Array.isArray(input.components)) throw new Error("Invalid completion components");
  const components = input.components;
  // Older responses retain the existing mounted-attempt behavior, never durable receipt authority.
  if (input.componentSources === undefined) return { sourceBound: false, effects: input.components.map((component, index) => ({ key: String(index), component, source: undefined as CompletionComponentSource | undefined })) };
  const sources = sourcesSchema.parse(input.componentSources);
  if (sources.ownerId !== input.ownerId || sources.effects.length !== input.components.length) throw new Error("Completion component owner or count changed");
  const requested = new Set(input.jobs.map(job => job.legacySource ? legacyWorkflowJobId(job.legacySource.sourceId) : job.jobId));
  const keys = new Set<string>();
  const fingerprints = new Map<string, string>();
  const effects = sources.effects.map((effect, index) => {
    if (!requested.has(effect.jobId) || (legacyWorkflowSourceId(effect.jobId) !== null
      ? effect.workflowResultId !== effect.jobId : effect.workflowResultId !== undefined)) throw new Error("Completion component source changed");
    const previous = fingerprints.get(effect.jobId);
    if (previous && previous !== effect.sourceFingerprint) throw new Error("Completion component revision changed");
    fingerprints.set(effect.jobId, effect.sourceFingerprint);
    const key = JSON.stringify([sources.ownerId, effect.jobId, effect.sourceFingerprint, effect.effectId]);
    if (keys.has(key)) throw new Error("Duplicate completion component effect");
    keys.add(key);
    return { key, component: components[index], source: effect as CompletionComponentSource | undefined };
  });
  return { sourceBound: true, effects };
}
